import { Box } from '@chakra-ui/react';
import type { SVGAttributes } from 'react';
import { memo, useRef, useEffect, useLayoutEffect, useState } from 'react';

export interface EmojiSvgRenderProps extends SVGAttributes<SVGSVGElement> {
  emoji: React.ReactNode;
  size?: SVGAttributes<SVGSVGElement>['width'];
}

export const EmojiSvgRender = memo(function EmojiSvgRender({
  size = '1em',
  emoji,
}: EmojiSvgRenderProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const emojiRef = useRef<HTMLSpanElement | null>(null);
  const [scale, setScale] = useState<number>(1);

  useLayoutEffect(() => {
    const container = boxRef.current;
    const emojiEl = emojiRef.current;
    if (!container || !emojiEl) return;

    // Purpose: Get container and emoji actual sizes
    // Reason: Calculate scale to fit emoji to container size
    const containerRect = container.getBoundingClientRect();
    const emojiRect = emojiEl.getBoundingClientRect();

    // Purpose: Calculate scale to fill container while maintaining aspect ratio
    // Reason: Scale emoji to match container size
    const containerSize = Math.min(containerRect.width, containerRect.height);
    const emojiSize = Math.max(emojiRect.width, emojiRect.height);

    if (emojiSize > 0) {
      // Apply some padding (e.g., 90% of container)
      const calculatedScale = (containerSize / emojiSize) * 0.9;
      setScale(calculatedScale);
    }
  }, [emoji, size]);

  useEffect(() => {
    if (typeof emoji === 'string') return;
    console.warn('EmojiSvgRender: emoji is not a string', emoji);
  }, [emoji]);

  return (
    <Box
      ref={boxRef}
      w={size}
      h={size}
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      <Box
        ref={emojiRef}
        as="span"
        display="inline-block"
        transform={`scale(${scale})`}
        transformOrigin="center center"
        style={{
          fontSize: '1em',
          lineHeight: 1,
        }}
      >
        {emoji}
      </Box>
    </Box>
  );
});
